import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { speak, initSpeechSynthesis } from '../utils/speech';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const CORRECTION_INTERVAL = 10000; // 10 seconds as requested by user

interface Props {
  isDetecting: boolean;
  sequenceId: string;
}

interface PoseInfo {
  id: string;
  name: string;
  description: string;
  instructions: string;
  tips: string[];
  time_remaining: number;
  total_duration: number;
  elapsed_time: number;
  next_pose: string | null;
  progress_percent: number;
}

// Define WebSocket message types
interface WSMessage {
  type: string;
  data: any;
}

const PoseSequenceDisplay: React.FC<Props> = ({ isDetecting, sequenceId }) => {
  const [currentPose, setCurrentPose] = useState<PoseInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isSequenceActive, setIsSequenceActive] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  
  const wsRef = useRef<WebSocket | null>(null);
  const hasStartedRef = useRef(false);
  const lastPoseIdRef = useRef<string | null>(null);  // Track last pose ID
  const initialInstructionsGivenRef = useRef<boolean>(false); // Track if initial instructions were given
  const isMounted = useRef(true);
  
  // Add a ref to track which pose IDs have had their instructions read
  // Using a Map to store pose IDs that have been read
  const poseInstructionsReadRef = useRef<Map<string, boolean>>(new Map());

  // Setup WebSocket connection
  const setupWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      console.log('WebSocket is already connected');
      return;
    }
    
    const wsUrl = `${BACKEND_URL.replace('http', 'ws')}/ws`;
    console.log(`Setting up WebSocket connection to ${wsUrl}`);
    
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onopen = () => {
      console.log('WebSocket connection established');
      setWsConnected(true);
      
      // Request current pose information
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'get_current_pose'
        }));
      }
    };
    
    wsRef.current.onclose = () => {
      console.log('WebSocket connection closed');
      setWsConnected(false);
      
      // Attempt to reconnect after a short delay
      setTimeout(() => {
        if (isMounted.current && isDetecting) {
          console.log('Attempting to reconnect WebSocket...');
          setupWebSocket();
        }
      }, 3000);
    };
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
      setError('WebSocket connection error. Please refresh the page.');
    };
    
    wsRef.current.onmessage = (event) => {
      const message: WSMessage = JSON.parse(event.data);
      
      switch (message.type) {
        case 'pose_update':
          handlePoseUpdate(message.data);
          break;
        
        case 'pose_transition':
          handlePoseTransition(message.data);
          break;
          
        case 'pose_correction':
          handlePoseCorrection(message.data);
          break;
          
        case 'sequence_complete':
          handleSequenceComplete();
          break;
          
        case 'error':
          console.error('WebSocket error from server:', message.data.message);
          setError(message.data.message);
          break;
          
        default:
          console.log('Unknown WebSocket message type:', message.type);
      }
    };
  }, [isDetecting]);
  
  // Handler for pose updates from WebSocket
  const handlePoseUpdate = useCallback((poseData: PoseInfo) => {
    console.log('Received pose update via WebSocket:', poseData.name);
    
    // Update the current pose state
    setCurrentPose(poseData);
    setError(null);
    
    // Timer debug
    if (poseData.time_remaining <= 10) {
      console.log(`⏱️ TIMER UPDATE: ${poseData.time_remaining} seconds remaining`);
    }
  }, []);
  
  // Handler for pose transitions from WebSocket
  const handlePoseTransition = useCallback((transitionData: any) => {
    const { old_pose_id, new_pose } = transitionData;
    console.log(`✨ NEW POSE TRANSITION! Previous: ${old_pose_id}, New: ${new_pose.id} (${new_pose.name})`);
    
    // Reset instruction flag
    initialInstructionsGivenRef.current = false;
    
    // Stop any ongoing speech
    window.speechSynthesis.cancel();
    
    // Dispatch transition event
    dispatchPoseTransition(new_pose.name);
    
    // Update the last pose ID reference
    lastPoseIdRef.current = new_pose.id;
    
    // Read pose instructions for this new pose - only once
    readPoseInstructions(new_pose);
    
    // Update the current pose state
    setCurrentPose(new_pose);
  }, []);
  
  // Handler for pose corrections from WebSocket
  const handlePoseCorrection = useCallback((correctionData: any) => {
    console.log('Received pose correction:', correctionData);
    
    // Speak the correction
    if (correctionData.correction) {
      speak(correctionData.correction, () => {
        console.log('Finished speaking correction');
      });
    }
  }, []);
  
  // Handler for sequence completion from WebSocket
  const handleSequenceComplete = useCallback(() => {
    console.log('✅ SEQUENCE COMPLETE');
    setIsSequenceActive(false);
    setCurrentPose(null);
    setError(null);
  }, []);

  // Simplified transition dispatch function - no locks
  const dispatchPoseTransition = (poseName: string) => {
    console.log(`DISPATCHING TRANSITION EVENT: Pose name = ${poseName}`);
    
    // Create and dispatch the event
    const transitionEvent = new CustomEvent('poseTransition', {
      detail: { poseName: poseName }
    });
    
    window.dispatchEvent(transitionEvent);
    console.log('TRANSITION EVENT DISPATCHED SUCCESSFULLY');
    
    return true;
  };

  // Function to read pose instructions once
  const readPoseInstructions = useCallback((pose: PoseInfo) => {
    // Skip if we've already read instructions for this pose
    if (poseInstructionsReadRef.current.get(pose.id)) {
      console.log(`🔊 SKIPPING INSTRUCTIONS FOR ${pose.name} - ALREADY READ`);
      return;
    }
    
    if (pose.instructions) {
      console.log(`🔊 READING INSTRUCTIONS FOR NEW POSE: ${pose.name}`);
      
      // Stop any ongoing speech first
      window.speechSynthesis.cancel();
      
      // Create instruction text including pose name
      const instructionText = `${pose.name}. ${pose.instructions}`;
      
      // Read the instructions
      speak(instructionText, () => {
        console.log(`🔊 FINISHED READING INSTRUCTIONS FOR: ${pose.name}`);
        
        // Dispatch a custom event to notify that introduction speech is completed
        const introCompletedEvent = new CustomEvent('introSpeechCompleted', {
          detail: { 
            poseName: pose.name,
            poseId: pose.id
          }
        });
        console.log(`🔊 DISPATCHING INTRO SPEECH COMPLETED EVENT FOR: ${pose.name}`);
        window.dispatchEvent(introCompletedEvent);
      });
      
      // Mark this pose's instructions as read
      poseInstructionsReadRef.current.set(pose.id, true);
    }
  }, []);

  // Replace the speakInstruction function with a wrapper for our speech utility
  const speakInstruction = useCallback((text: string) => {
    if (!isMounted.current) return;
    
    // Use the standardized speech utility
    setSpeaking(true);
    const utterance = speak(text, () => {
      if (!isMounted.current) return;
      setSpeaking(false);
    });
    
    return utterance;
  }, []);

  // Setup WebSocket and handle detection state changes
  useEffect(() => {
    if (isDetecting && !hasStartedRef.current) {
      hasStartedRef.current = true;
      // Setup WebSocket connection
      setupWebSocket();
      // Set sequence as active
      setIsSequenceActive(true);
    } else if (!isDetecting && hasStartedRef.current) {
      // When detection stops, close WebSocket but keep sequence active
      hasStartedRef.current = false;
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    }
    
    return () => {
      // Clean up WebSocket on component unmount
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isDetecting, setupWebSocket]);

  // Make sure we initialize the speech synthesis in a useEffect hook
  useEffect(() => {
    isMounted.current = true;
    
    // Initialize speech synthesis with consistent voice
    initSpeechSynthesis();
    
    return () => {
      isMounted.current = false;
      window.speechSynthesis.cancel();
      
      // Clean up WebSocket on component unmount
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  // This function is ONLY called when user manually presses the Next button
  const handleNextPose = async () => {
    try {
      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      console.log('📡 MANUAL NEXT BUTTON PRESSED - SENDING NEXT POSE REQUEST TO API...');
      const response = await axios.post(`${BACKEND_URL}/api/sequence/next`);
      console.log('📡 API RESPONSE:', response.data);
      
      // If sequence is complete, clean up
      if (response.data.status === 'complete') {
        if (wsRef.current) {
          wsRef.current.close();
          wsRef.current = null;
        }
        
        console.log('✅ SEQUENCE COMPLETE');
        setIsSequenceActive(false);
        setCurrentPose(null);
        setError(null);
        return;
      }
      
      // Reset the pose ID reference to force a correction refresh
      lastPoseIdRef.current = '';
      
      // For manual transitions, we use the transition instructions from the API
      // rather than the pose instructions
      if (response.data.instructions && response.data.instructions.length > 0) {
        // Combine instructions into one coherent message to avoid queuing
        const combinedInstructions = response.data.instructions.join(" ");
        
        console.log('🔊 SPEAKING TRANSITION INSTRUCTIONS...');
        // Use the standardized speech utility without tracking state
        speak(combinedInstructions, () => {
          console.log('🔊 FINISHED SPEAKING TRANSITION INSTRUCTIONS');
        });
      }
      
      // No need to fetch the new pose info, WebSocket will handle it
    } catch (err) {
      console.error('❌ ERROR TRANSITIONING POSE:', err);
      setError('Failed to transition to next pose. Please try again.');
    }
  };

  // Start sequence handler is now just setting state
  const handleStartSequence = () => {
    setIsSequenceActive(true);
  };

  // New function to send pose detection data via WebSocket
  const sendPoseDetection = (keypoints: any, poseId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'pose_detection',
        data: {
          keypoints: keypoints,
          current_pose_id: poseId
        }
      }));
    }
  };

  // Listen for the pose detection event
  useEffect(() => {
    const handlePoseDetectionEvent = (event: CustomEvent) => {
      const { keypoints, poseId } = event.detail;
      
      // Only send if we have a valid pose ID and WebSocket is connected
      if (poseId && wsRef.current?.readyState === WebSocket.OPEN) {
        sendPoseDetection(keypoints, poseId);
      }
    };
    
    // Add event listener
    window.addEventListener('poseDetection', handlePoseDetectionEvent as EventListener);
    
    // Clean up
    return () => {
      window.removeEventListener('poseDetection', handlePoseDetectionEvent as EventListener);
    };
  }, []);

  // Format time nicely (convert seconds to MM:SS format)
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  // If there's an error, show it
  if (error) {
    return (
      <div style={{ 
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        right: '20px',
        textAlign: 'center',
        padding: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        borderRadius: '10px',
        zIndex: 100
      }}>
        <p style={{ margin: 0, color: '#dc3545' }}>{error}</p>
        <button 
          onClick={handleStartSequence}
          style={{
            marginTop: '10px',
            padding: '8px 15px',
            backgroundColor: '#17a2b8',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            cursor: 'pointer'
          }}
        >
          Start Sequence
        </button>
      </div>
    );
  }

  // If sequence hasn't started yet, show the start message
  if (!isSequenceActive && !currentPose) {
    
  }

  // If we're loading the first pose, show loading
  if (loading && !currentPose) {
    return (
      <div style={{ 
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        right: '20px',
        textAlign: 'center',
        padding: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        borderRadius: '10px',
        zIndex: 100
      }}>
        <p style={{ margin: 0, fontSize: '14px' }}>Loading next pose...</p>
      </div>
    );
  }

  // If we have a current pose, show it (even if loading, to prevent flicker)
  if (currentPose) {
    return (
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '20px',
        right: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '15px',
        borderRadius: '10px',
        zIndex: 100,
        fontFamily: 'Arial, sans-serif'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
          <h2 style={{ margin: 0 }}>{currentPose.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ 
              backgroundColor: '#28a745', 
              padding: '8px 15px', 
              borderRadius: '20px',
              fontWeight: 'bold',
              fontSize: '18px'
            }}>
              {formatTime(currentPose.time_remaining)}
            </div>
          </div>
        </div>
        
        <p style={{ marginBottom: '10px' }}>{currentPose.description}</p>
        
        <div style={{ marginBottom: '15px' }}>
          <h4 style={{ marginBottom: '5px', marginTop: 0 }}>Instructions:</h4>
          <p style={{ margin: 0 }}>{currentPose.instructions}</p>
        </div>
        
        {currentPose.tips.length > 0 && (
          <div style={{ marginBottom: '15px' }}>
            <h4 style={{ marginBottom: '5px', marginTop: 0 }}>Tips:</h4>
            <ul style={{ marginTop: '5px' }}>
              {currentPose.tips.map((tip, index) => (
                <li key={index}>{tip}</li>
              ))}
            </ul>
          </div>
        )}
        
        <div style={{ marginTop: '10px' }}>
          <div style={{ 
            width: '100%', 
            height: '10px', 
            backgroundColor: '#444',
            borderRadius: '5px', 
            overflow: 'hidden' 
          }}>
            <div style={{ 
              width: `${currentPose.progress_percent}%`, 
              height: '100%', 
              backgroundColor: '#28a745',
              transition: 'width 0.5s ease'
            }} />
          </div>
        </div>
        
        <div style={{ 
          marginTop: '15px', 
          display: 'flex', 
          justifyContent: 'space-between' 
        }}>
          <button 
            onClick={handleStartSequence}
            style={{
              padding: '8px 15px',
              backgroundColor: '#17a2b8',
              color: 'white',
              border: 'none',
              borderRadius: '5px',
              cursor: 'pointer'
            }}
          >
            Start Sequence
          </button>
          
          {currentPose.next_pose && (
            <button 
              onClick={handleNextPose}
              style={{
                padding: '8px 15px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Next: {currentPose.next_pose}
            </button>
          )}
          {!currentPose.next_pose && (
            <button 
              onClick={handleNextPose}
              style={{
                padding: '8px 15px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer'
              }}
            >
              Finish Sequence
            </button>
          )}
        </div>
      </div>
    );
  }

  // If nothing else matches, return null
  return null;
};

export default PoseSequenceDisplay; 